import { useEffect, useState } from "react";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Users, ImageIcon, QrCode, Settings } from "lucide-react";
import GuestsTab from "@/pages/admin/GuestsTab";
import PhotosTab from "@/pages/admin/PhotosTab";
import TablesTab from "@/pages/admin/TablesTab";
import SettingsTab from "@/pages/admin/SettingsTab";

const tabs = [
  { id: "guests", label: "Gäster", icon: Users },
  { id: "photos", label: "Foton", icon: ImageIcon },
  { id: "tables", label: "QR-koder", icon: QrCode },
  { id: "settings", label: "Inställningar", icon: Settings },
];

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem("admin_token", data.token);
      onLogin(data.username);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-xs text-center">
        <p className="font-script text-5xl" style={{ color: "var(--ink)" }}>
          Sarah &amp; Rafid
        </p>
        <p className="label-caps text-[0.65rem] mt-3 mb-10" style={{ color: "var(--gold)" }}>
          Administration
        </p>
        <input
          className="input-line text-center mb-5"
          placeholder="Användarnamn"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          data-testid="admin-username-input"
        />
        <input
          className="input-line text-center mb-10"
          type="password"
          placeholder="Lösenord"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          data-testid="admin-password-input"
        />
        <button className="btn-invite btn-solid w-full" disabled={loading} data-testid="admin-login-btn">
          {loading ? "Loggar in…" : "Logga in"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("guests");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data.username))
      .catch(() => localStorage.removeItem("admin_token"))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="min-h-screen" />;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen font-sans-d" data-testid="admin-dashboard">
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{ backgroundColor: "rgba(249,246,240,0.9)", borderBottom: "1px solid rgba(212,185,160,0.4)" }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <p className="font-script text-2xl" style={{ color: "var(--ink)" }}>
            Sarah &amp; Rafid
          </p>
          <button
            className="flex items-center gap-1.5 text-xs opacity-70 hover:opacity-100 transition-opacity"
            onClick={() => {
              localStorage.removeItem("admin_token");
              setUser(null);
            }}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Logga ut
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="flex items-center gap-1.5 px-3.5 py-2.5 text-xs whitespace-nowrap transition-colors"
              style={{
                borderBottom: tab === t.id ? "2px solid var(--ink)" : "2px solid transparent",
                color: tab === t.id ? "var(--ink)" : "var(--ink-soft)",
                fontWeight: tab === t.id ? 600 : 400,
              }}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-8">
        {tab === "guests" && <GuestsTab />}
        {tab === "photos" && <PhotosTab />}
        {tab === "tables" && <TablesTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
